<template>
  <div class="auth-block"
       :class="accent ? '_accent' : null"
  >
    <div class="auth-block__inner">
      <form @keypress.enter="onClick" class="auth-block__content">
        <BasePreloader v-if="pending"/>
        <h2 class="auth-block__title">{{ $tAuth('Registration') }}</h2>
        <div class="auth-block__roles">
          <div class="auth-block__roles-label">{{ $tAuth('Choose your role') }}</div>
          <div class="auth-block__roles-list">
            <button type="button"
                    v-for="item in roles"
                    :key="item.id"
                    :class="currentRoleComputed === item.id ? '_active' : null"
                    class="auth-block__role-btn base-btn base-btn--outline-grey-green"
                    @click.prevent="onClickRole(item.id)"
            >
              <span class="base-btn__text">{{ $tAuth(item.name) }}</span>
              <span class="tick">
                <span>
                  <BaseSvg name="ico-check"/>
                </span>
              </span>
            </button>
          </div>
        </div>
        <BaseInput v-model="inputs.email.value"
                   :required="inputs.email.required"
                   :error="$tAuth(inputs.email.error)"
                   :label="$tAuth('E-mail')"
                   class="auth-block__input"
                   name="email"
                   :class="accent ? '_white _shadow' : null"
        />
        <BaseInputPhone :value="inputs.phone.value"
                        :label="$tAuth('Phone number')"
                        :error="$tAuth(inputs.phone.error)"
                        @input="onPhoneInput"
                        class="phone-input auth-block__input"
                        white
        />
        <BaseInput v-model="inputs.password.value"
                   :required="inputs.password.required"
                   :error="$tAuth(inputs.password.error)"
                   :label="$tAuth('Password')"
                   name="password"
                   type="password"
                   class="auth-block__input"
                   :class="accent ? '_white _shadow' : null"
        />
        <div class="auth-block__password-info password-info">
          <BaseSvg name="ico-info" class="password-info__ico"/>
          <span class="password-info__text">{{ $tAuth('Your password must contain') }}:</span>
          <ul class="password-info__list">
            <li :class="{'_active': passwordLongEnough}"
                class="password-info__list-item">
              <BaseSvg name="ico-check"/>
              <span>{{ $tAuth('At least 6 characters') }}</span>
            </li>
            <li :class="{'_active': passwordHasNumber}"
                class="password-info__list-item">
              <BaseSvg name="ico-check"/>
              <span>{{ $tAuth('Number') }}</span>
            </li>
            <li :class="{'_active': passwordHasLetter}"
                class="password-info__list-item">
              <BaseSvg name="ico-check"/>
              <span>{{ $tAuth('Letter') }}</span>
            </li>
          </ul>
        </div>
        <BaseInput v-model="passwordRepeat.value"
                   :required="inputs.password.required"
                   :error="$tAuth(passwordRepeat.error)"
                   :label="$tAuth('Confirm password')"
                   class="auth-block__input"
                   name="confirm_password"
                   type="password"
                   :class="accent ? '_white _shadow' : null"
                   @keypress.enter="onClick"
        />
        <BaseCheckbox v-model="agree"

                      class="auth-block__checkbox"
                      :class="accent ? '_white _shadow' : null"
        >
          <div slot="label" class="checkbox__text">
            {{ $tAuth('I agree to the') }}
            <router-link-modified :to="{name:'privacy-policy'}"
                                  tag="a">
              {{ $tAuth('user agreement') }}
            </router-link-modified>&nbsp;
            {{ $tAuth('and') }}&nbsp;
            <router-link-modified :to="{name:'terms-of-use'}"
                                  tag="a">
              {{ $tAuth('privacy policy') }}
            </router-link-modified>
          </div>
        </BaseCheckbox>

        <BaseAlert :value="$tAuth(overallErrorsMix)"
                   class="auth-block__alert  _error">
          <template v-if="errorStatusCode"
                    v-slot:li="{formattedValue}">
            <li class="alert__list-item">
              {{ $tAuth(overallErrorsMix) }}
              <a href="#reset-password"
                 @click.prevent="onResetPasswordClick">
                {{ $tAuth('Reset password') }}
              </a>
            </li>
          </template>
        </BaseAlert>

        <button type="button"
                @click.prevent="onClick"
                class="auth-block__btn-submit base-btn base-btn--blue  specific-registration">
          <span class="base-btn__text">{{ $tAuth('Sign up') }}</span>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
  import {EMPLOYEE, PARTNER, EMPLOYER} from '../constants/usersTypes'
  import formMixins from '../mixins/formMixins'

  const roles = [{
    id: EMPLOYEE,
    name: 'Job seeker'
  }, {
    id: PARTNER,
    name: 'Agent'
  }, {
    id: EMPLOYER,
    name: 'Employer'
  }]

  export default {
    mixins: [formMixins],
    props: {
      accent: {
        type: Boolean,
        default: false
      },
      previousPage: Object
    },
    data() {
      return {
        pending: false,
        errorStatusCode: null,
        inputs: {
          user_type: {
            value: roles[0].id,
            error: null
          },
          email: {
            value: null,
            error: null,
            required: true
          },
          phone: {
            value: null,
            error: null
          },
          password: {
            value: '',
            error: null,
            required: true
          },
          referal_code: {
            value: ''
          }
        },
        passwordRepeat: {
          value: null,
          error: null
        },
        agree: true,
        roles
      }
    },
    computed: {
      currentRoleComputed() {
        return this.inputs.user_type.value || this.roles[0].id
      },
      passwordLongEnough() {
        return this.inputs.password.value && this.inputs.password.value.length >= 6
      },
      passwordHasNumber() {
        return /\d/.test(this.inputs.password.value)
      },
      passwordHasLetter() {
        return /[A-Za-z]/.test(this.inputs.password.value)
      },
      comparePassword() {
        return this.inputs.password.value === this.passwordRepeat.value
      },
      checkPhone() {
        if (!this.inputs.phone.value) return false
        return this.inputs.phone.value.startsWith('+')
      }
    },
    methods: {
      onClick() {
        this.resetErrorsMix()
        this.errorStatusCode = null
        this.passwordRepeat.error = null

        const sendData = {}
        sendData.user_type = this.inputs.user_type.value
        sendData.email = this.inputs.email.value
        sendData.password = this.inputs.password.value
        sendData.referal_code = this.inputs.referal_code.value

        if (this.checkPhone) {
          sendData.phone = this.inputs.phone.value.substring(1)
        } else {
          sendData.phone = this.inputs.phone.value
        }

        if (!this.passwordLongEnough ||
          !this.passwordHasNumber ||
          !this.passwordHasLetter) {
          return this.inputs.password.error = 'password-error'
        }

        // if (this.isRequiredEmptyMix) {
        //   this.overallErrorsMix = 'Some fields is not correct'
        //   return
        // }

        if (!this.comparePassword) {
          this.passwordRepeat.error = 'compare-password-error'
          return
        }

        if (!this.agree) {
          this.overallErrorsMix = 'Please agree rules'
          return
        }

        this.pending = true
        this.$store.dispatch('user/registerUser', {sendData}).then(() => {
          this.afterLogin()
        }).catch((error => {
          if (error.response && error.response.data && error.response.data.message_code) {
            this.errorStatusCode = error.response.data.message_code

          }
          this.errorHandlerMix(error)
        })).finally(() => {
          this.pending = false;
        })
      },
      onClickRole(itemId) {
        this.inputs.user_type.value = itemId
        this.$store.dispatch('user/chooseUserType', this.currentRoleComputed)
      },
      afterLogin() {
        this.$store.dispatch('user/loadUser').then((data) => {
          const userType = data.user.user_type
          const matchedRoute = this.$route.query.redirect ? this.$router.matcher.match(this.$route.query.redirect) : null

          if (
            matchedRoute
            && matchedRoute.name
          ) {
            if (!matchedRoute.meta.requiredUserTypes
              || !matchedRoute.meta.requiredUserTypes.length
              || matchedRoute.meta.requiredUserTypes.includes(userType)) {
              return this.$router.push({path: this.$route.query.redirect})
            } else {
              const title = 'Для вашего аккаунта этот раздел закрыт'
              const message = userType === EMPLOYEE
                ? 'Вы авторизировались как соискатель. Для перехода в этот раздел вам нужно авторизировтся как роботодатель.'
                : 'Вы авторизировались как роботодатель. Для перехода в этот раздел вам нужно авторизировтся как соискатель.'
              this.$vModal.open('warning', {
                title,
                message
              })
            }
          }

          if (this.previousPage && this.previousPage.name !== 'home') {
            this.$router.push(this.previousPage.fullPath)
          } else {
            if (data.user.user_type === EMPLOYEE) {
              this.$router.push({name: 'account-resumes'})
            } else {
              this.$router.push({name: 'account-vacancies'})
            }
          }
        })
      },
      onResetPasswordClick() {
        this.$vModal.open('forgot-pass', {
          defaultEmailPhone: this.errorStatusCode === 1 ? this.inputs.email.value :
            this.errorStatusCode === 2 ? this.inputs.phone.value :
              null
        })
      },
      onPhoneInput(value) {
        this.inputs.phone.value = value
      }
    },
    created() {
      let startUserType = +this.$route.query.user_type
      if (!isNaN(startUserType)) {
        startUserType = roles.find(item => item.id === startUserType)
      }

      if (startUserType) {
        this.inputs.user_type.value = startUserType.id
      }

      if (this.$route.query.referal_code) {
        this.inputs.referal_code.value = this.$route.query.referal_code
      }
    }
  }
</script>

<style lang="less">

</style>
